// PAAF-export required patient field codes (see service/admin/analytic/paaf.go validatePaafFields).
// Static superset of the common (initial/annual) case; clinician fields are always
// required here (unalignment-only exports omit them server-side).
// alignmentDate is gated separately in isPatientFieldRequired to post-alignment patients only.
const PAAF_REQUIRED_CODES = [
  "firstName", "lastName", "dateOfBirth", "medicareBeneficiaryIdentifier",
  "alignmentDate", "diagnosedWithDementia",
  "diagnosedWithDementiaClinicianFirstName",
  "diagnosedWithDementiaClinicianLastName",
  "diagnosedWithDementiaClinicianNPI",
  "email", "phone", "phoneType", "existingPatient", "patientHasPCP",
  "address", "city", "state", "postalCode",
  "patientResidence", "eligibleResidenceAttestation",
];

// PAAF-export required caregiver field codes (see validatePaafFields caregiver
// blocks). Hours/health/mental are required on initial/annual when a caregiver
// exists and on caregiver-axis reassessments. MBI and length-in-role are gated
// in isCaregiverFieldRequired.
const PAAF_REQUIRED_CAREGIVER_CODES = [
  "firstName", "lastName", "phone", "phoneType", "dateOfBirth",
  "gender", "relationshipToPatient", "liveWithPatient", "medicareBeneficiary",
  "caregivingHours", "overallHealth", "overallMentalHealth",
];

// patientResidence answers that identify an RCC beneficiary, for whom the RCC
// Partner ID is required (blank/optional for a Private residence).
const RCC_RESIDENCE_VALUES = ["RCC - Assisted living facility", "RCC - Other"];

const isRCCResidence = (residence) => RCC_RESIDENCE_VALUES.includes(residence);

const MISSING_FIELD_CLASS = "border-red-500 bg-red-50";

function isEmpty(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime());
  }
  return !String(value ?? "").trim();
}

function isPatientFieldRequired(code, { lifecycleStage, patientResidence } = {}) {
  // Alignment date is only required (and red-highlighted when missing) once
  // the patient has entered Post-Alignment; pre/unaligned patients may omit it.
  if (code === "alignmentDate") {
    return lifecycleStage === "POST_ALIGNMENT_NEW" || lifecycleStage === "POST_ALIGNMENT_EST";
  }
  if (PAAF_REQUIRED_CODES.includes(code)) return true;
  if (code === "rccPartnerId") return isRCCResidence(patientResidence);
  return false;
}

function isPatientFieldMissing(code, answer, ctx) {
  return isPatientFieldRequired(code, ctx) && isEmpty(answer);
}

function isCaregiverFieldRequired(code, answers = {}) {
  if (PAAF_REQUIRED_CAREGIVER_CODES.includes(code)) return true;
  if (code === "medicareBeneficiaryIdentifier") {
    return answers.medicareBeneficiary === "Yes";
  }
  // CMS: at least one of years or months in role; highlight both when both blank.
  if (code === "yearsInRole" || code === "monthsInRole") {
    return isEmpty(answers.yearsInRole) && isEmpty(answers.monthsInRole);
  }
  return false;
}

function isCaregiverFieldMissing(code, answers = {}) {
  return isCaregiverFieldRequired(code, answers) && isEmpty(answers[code]);
}

export {
  PAAF_REQUIRED_CODES,
  PAAF_REQUIRED_CAREGIVER_CODES,
  RCC_RESIDENCE_VALUES,
  MISSING_FIELD_CLASS,
  isRCCResidence,
  isEmpty,
  isPatientFieldRequired,
  isPatientFieldMissing,
  isCaregiverFieldRequired,
  isCaregiverFieldMissing,
};
